import { useState, useSyncExternalStore } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { UserPlus } from 'lucide-react'
import { formatearFecha } from '@/lib/fecha'
import { Boton } from '@/ui/Boton'
import { CampoFormulario, CampoTexto, Selector } from '@/ui/CampoFormulario'
import { ConfirmacionDestructiva } from '@/ui/ConfirmacionDestructiva'
import { Esqueleto } from '@/ui/Esqueleto'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { cerrarSesion, sesionActual, suscribirse } from '@/dominio/sesion/sesion'
import {
  useAgregarMiembro,
  useCambiarRolMiembro,
  useEquipoDespacho,
  useRetirarMiembro,
} from '@/dominio/despacho/useDespacho'
import { NOMBRE_ROL, type MiembroDespacho, type RolDespacho } from '@/dominio/despacho/tipos'
import { EncabezadoPantalla, Tarjeta } from '../-pantalla'
import { ErrorDelDespacho, ErrorEnLinea } from './-comun'

/**
 * El equipo del despacho (`docs/adr/0021` §1): titulares, que ven toda la
 * cartera, invitan y asignan; y auxiliares, que solo ven lo que se les asigna.
 *
 * Agregar a alguien sin cuenta le crea una con la credencial inicial que se
 * teclea aquí, como el alta de usuarios de la consola: se le entrega por un
 * canal seguro y la cambia al entrar. **El despacho no se queda sin titular**:
 * la base no deja degradar ni retirar al último.
 */
export const Route = createFileRoute('/despacho/equipo')({
  component: PantallaEquipo,
})

const CREDENCIAL_MINIMA = 12

function PantallaEquipo() {
  const sesion = useSyncExternalStore(suscribirse, sesionActual, () => null)
  const esTitular = sesion?.despacho?.rol === 'titular'
  const equipo = useEquipoDespacho()
  const cambiarRol = useCambiarRolMiembro()
  const retirar = useRetirarMiembro()
  const [retirando, setRetirando] = useState<MiembroDespacho | null>(null)

  const vigentes = (equipo.data ?? []).filter((m) => m.retiradoEl === null)
  const anteriores = (equipo.data ?? []).filter((m) => m.retiradoEl !== null)
  const yo = vigentes.find((m) => m.email.toLowerCase() === sesion?.email.toLowerCase())

  return (
    <div className="flex max-w-[960px] flex-col gap-6">
      <EncabezadoPantalla
        titulo="Equipo"
        descripcion="Quién del despacho entra a la cartera. Dentro de cada cliente, todos trabajan con los permisos de contador."
      />

      {esTitular ? <AgregarMiembro /> : null}

      {cambiarRol.isError ? <ErrorEnLinea error={cambiarRol.error} accion="cambiar el rol" /> : null}
      {retirar.isError ? <ErrorEnLinea error={retirar.error} accion="retirar a la persona" /> : null}

      <Tarjeta titulo={`En el equipo (${vigentes.length})`} relleno={false}>
        {equipo.isError ? (
          <ErrorDelDespacho error={equipo.error} queCargaba="el equipo" onReintentar={() => void equipo.refetch()} />
        ) : equipo.isLoading ? (
          <Esqueleto className="m-4 h-24" />
        ) : (
          <table className="w-full border-collapse text-cuerpo">
            <thead className="bg-gris-50">
              <tr className="border-b border-gris-300 text-left text-etiqueta font-semibold text-gris-600">
                <th scope="col" className="px-3 py-2">Persona</th>
                <th scope="col" className="w-[160px] px-3 py-2">Rol</th>
                <th scope="col" className="w-[120px] px-3 py-2 text-right">Clientes asignados</th>
                <th scope="col" className="w-[120px] px-3 py-2">Desde</th>
                <th scope="col" className="w-[120px] px-3 py-2">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {vigentes.map((m) => (
                <tr key={m.miembroId} className="border-b border-gris-200 last:border-b-0">
                  <td className="px-3 py-2 text-gris-900">{m.email}</td>
                  <td className="px-3 py-2">
                    {esTitular ? (
                      <select
                        value={m.rol}
                        disabled={cambiarRol.isPending}
                        onChange={(e) => cambiarRol.mutate({ miembro: m.miembroId, rol: e.target.value as RolDespacho })}
                        aria-label={`Rol de ${m.email}`}
                        className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta"
                      >
                        <option value="titular">Titular</option>
                        <option value="auxiliar">Auxiliar</option>
                      </select>
                    ) : (
                      <InsigniaEstado tono={m.rol === 'titular' ? 'marca' : 'neutral'}>{NOMBRE_ROL[m.rol]}</InsigniaEstado>
                    )}
                  </td>
                  <td className="numero px-3 py-2 text-right text-gris-700">
                    {m.rol === 'titular' ? 'todos' : m.clientesAsignados}
                  </td>
                  <td className="numero px-3 py-2 text-gris-700">{formatearFecha(m.activoDesde)}</td>
                  <td className="px-3 py-2">
                    {esTitular || m === yo ? (
                      <Boton tamano="menor" variante="discreto" onClick={() => setRetirando(m)}>
                        {m === yo ? 'Salir' : 'Retirar'}
                      </Boton>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Tarjeta>

      {anteriores.length > 0 ? (
        <Tarjeta titulo="Salieron en el último año">
          <ul className="flex flex-col gap-1 text-cuerpo text-gris-700">
            {anteriores.map((m) => (
              <li key={m.miembroId}>
                {m.email} · {NOMBRE_ROL[m.rol].toLowerCase()} hasta el {formatearFecha(m.retiradoEl)}
              </li>
            ))}
          </ul>
        </Tarjeta>
      ) : null}

      {retirando ? (
        <ConfirmacionDestructiva
          abierto
          onCancelar={() => setRetirando(null)}
          onConfirmar={() =>
            retirar.mutate(retirando.miembroId, {
              onSuccess: () => {
                // Quien se retira a sí mismo ya no tiene despacho: su testigo deja de servir.
                if (retirando === yo) cerrarSesion()
                setRetirando(null)
              },
            })
          }
          titulo={retirando === yo ? 'Salir del despacho' : 'Retirar del equipo'}
          descripcion={
            <p>
              <strong>{retirando.email}</strong> deja de entrar a la cartera y a todos sus clientes desde ya, y pierde sus
              asignaciones. Su cuenta sigue existiendo.
            </p>
          }
          textoAEscribir="retirar"
          etiquetaConfirmar={retirando === yo ? 'Salir' : 'Retirar'}
          trabajando={retirar.isPending}
        />
      ) : null}
    </div>
  )
}

function AgregarMiembro() {
  const agregar = useAgregarMiembro()
  const [email, setEmail] = useState('')
  const [rol, setRol] = useState<RolDespacho>('auxiliar')
  const [credencial, setCredencial] = useState('')

  const enviar = (evento: React.FormEvent): void => {
    evento.preventDefault()
    agregar.mutate(
      { email: email.trim(), rol, credencialInicial: credencial === '' ? undefined : credencial },
      {
        onSuccess: () => {
          setEmail('')
          setCredencial('')
        },
      },
    )
  }

  return (
    <Tarjeta titulo="Agregar al equipo">
      <form onSubmit={enviar} className="flex flex-col gap-3">
        <div className="grid gap-3 md:grid-cols-[1fr_180px]">
          <CampoTexto etiqueta="Correo" obligatorio type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <CampoFormulario etiqueta="Rol" obligatorio>
            {(atributos) => (
              <Selector {...atributos} value={rol} onChange={(e) => setRol(e.target.value as RolDespacho)}>
                <option value="auxiliar">Auxiliar · lo asignado</option>
                <option value="titular">Titular · toda la cartera</option>
              </Selector>
            )}
          </CampoFormulario>
        </div>
        <CampoTexto
          etiqueta="Credencial inicial"
          anotacion="solo si la persona no tiene cuenta"
          type="password"
          autoComplete="new-password"
          value={credencial}
          onChange={(e) => setCredencial(e.target.value)}
          error={credencial !== '' && credencial.length < CREDENCIAL_MINIMA ? `Al menos ${CREDENCIAL_MINIMA} caracteres.` : undefined}
          ayuda="Si el correo ya tiene cuenta en la plataforma, se le agrega con la suya y esto no se usa."
        />
        {agregar.isSuccess ? (
          <p className="text-etiqueta text-verde">
            {agregar.data.resultado === 'ya_era'
              ? 'Esa persona ya estaba en el equipo con ese rol.'
              : agregar.data.cuentaNueva
                ? 'Agregada, con cuenta nueva. Entréguele la credencial inicial por un canal seguro.'
                : 'Agregada con su cuenta de siempre.'}
          </p>
        ) : null}
        {agregar.isError ? <ErrorEnLinea error={agregar.error} accion="agregar a la persona" /> : null}
        <div>
          <Boton type="submit" variante="primario" icono={<UserPlus />} disabled={!email.includes('@') || agregar.isPending}>
            {agregar.isPending ? 'Agregando…' : 'Agregar'}
          </Boton>
        </div>
      </form>
    </Tarjeta>
  )
}
